import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { GTMEventNames } from 'src/constants/GTMEventNames';
import { DayPartEnum, IDaypart, ScreenSize } from 'src/interfaces';

export let dataLayer = window.dataLayer || [];

export const useGoogleTagManager = () => {
  const { pathname } = useLocation();
  const isPageViewed = useRef(false);

  useEffect(() => {
    const currentPageTitle = getPageTitle(pathname);
    const title = 'Creative Center';

    document.title = currentPageTitle
      ? `${title} - ${currentPageTitle}`
      : title;

    if (isPageViewed.current) {
      // send custom event {pageview} to GTM
      dataLayer.push({
        event: 'pageview',
        page: {
          title: document.title,
          location:
            window.location.origin + window.location.pathname + '#' + pathname,
          path: window.location.pathname + '#' + pathname,
        },
      });
    } else {
      isPageViewed.current = true;
    }
  }, [pathname]);
};

export const getPageTitle = (pathname: string) => {
  // TODO: get the paths from the constant/paths.ts
  // Do not reorder the page titles
  const pageTitles = [
    { path: '/components/add', title: 'Components - Add' },
    { path: '/components/edit', title: 'Components - Edit' },
    { path: '/components/details', title: 'Components - Details' },
    { path: '/components', title: 'Components' },
    { path: '/locations/add/group', title: 'Locations - Add - Group' },
    {
      path: /\/locations\/group\/\d+\/timings/,
      title: 'Locations - Group - Timings',
    },
    {
      path: /\/locations\/group\/\d+\/assign/,
      title: 'Locations - Group - Assign',
    },
    {
      path: /\/locations\/group\/\d+\/schedule/,
      title: 'Locations - Group - Schedule',
    },
    { path: /\/locations\/\d+\/timings/, title: 'Locations - Timings' },
    { path: /\/locations\/\d+\/schedule/, title: 'Locations - Schedule' },
    { path: '/locations/add', title: 'Locations - Add' },
    { path: '/locations', title: 'Locations' },
    { path: '/assemblies/add', title: 'Assemblies - Add' },
    { path: '/assemblies/edit', title: 'Assemblies - Edit' },
    { path: '/assemblies/bulk-replace', title: 'Assemblies - Bulk Replace' },
    { path: '/assemblies', title: 'Assemblies' },
    { path: '/products/details', title: 'Products - Details' },
    { path: '/products/add', title: 'Products - Add' },
    { path: '/products/edit', title: 'Products - Edit' },

    { path: '/products/categories/add', title: 'Products - Categories - Add' },
    {
      path: '/products/categories/edit',
      title: 'Products - Manage Product Category - Edit',
    },
    {
      path: '/products/categories/manage',
      title: 'Products - Manage Product Category',
    },

    { path: '/products', title: 'Products' },
    { path: '/settings/configurations', title: 'Settings - Configurations' },
    { path: '/settings/accessibility', title: 'Settings - Accessibility' },
    { path: '/settings/actionTracking', title: 'Settings - Action Tracking' },
    { path: '/settings', title: 'Settings' },
  ];

  return pageTitles.find((page) => pathname.match(page.path))?.title;
};

export const createAssemblyEvent = (
  assembly_id: number,
  assembly_name: string,
  layout_type: string,
  layout_name: string,
) => {
  dataLayer.push({
    event: GTMEventNames.ASSEMBLY_CREATED,
    assembly_id,
    assembly_name,
    layout_type,
    layout_name,
  });
};

export const saveReadyToUseAssemblyEvent = (assembly_id: number) => {
  dataLayer.push({
    event: GTMEventNames.ASSEMBLY_SAVED_READY_TO_USE,
    assembly_id,
  });
};

export const createComponentEvent = (
  component_id: number,
  component_type: string,
  component_name: string,
  layout_type: string,
) => {
  dataLayer.push({
    event: GTMEventNames.COMPONENT_CREATED,
    component_id,
    component_type,
    component_name,
    layout_type,
  });
};

export const saveComponentReadyToUseEvent = (component_id: number) => {
  dataLayer.push({
    event: GTMEventNames.COMPONENT_SAVED_READY_TO_USE,
    component_id,
  });
};

export const saveComponentAsDraftEvent = (component_id: number) => {
  dataLayer.push({
    event: GTMEventNames.COMPONENT_SAVED_DRAFT,
    component_id,
  });
};

export const componentEditFromCardEvent = (component_id: number) => {
  dataLayer.push({
    event: GTMEventNames.COMPONENT_EDIT_FROM_CARD,
    component_id,
  });
};

export const componentConvertToDraftFromCardEvent = (component_id: number) => {
  dataLayer.push({
    event: GTMEventNames.COMPONENT_CONVERT_TO_DRAFT_FROM_CARD,
    component_id,
  });
};

export const componentConvertToDraftFromInfoEvent = (component_id: number) => {
  dataLayer.push({
    event: GTMEventNames.COMPONENT_CONVERT_TO_DRAFT_FROM_INFO,
    component_id,
  });
};

export const enterEditComponentFromInfoEvent = (component_id: number) => {
  dataLayer.push({
    event: GTMEventNames.ENTER_EDIT_COMPONENT_FROM_INFO,
    component_id,
  });
};

export const replaceComponentWithinAssemblyEvent = (
  assembly_id: string | number,
  old_component_id: string | number,
  new_component_id: string | number,
) => {
  dataLayer.push({
    event: GTMEventNames.ASSEMBLY_REPLACE_COMPONENT,
    assembly_id,
    component_id_old: old_component_id,
    component_id_new: new_component_id,
  });
};

export const assemblyEditFromRowEvent = (assembly_id: number) => {
  dataLayer.push({
    event: GTMEventNames.ASSEMBLY_EDIT_FROMCARD,
    assembly_id,
  });
};
export const scheduleAssemblyEvent = (
  location_id?: string,
  location_group_id?: string,
  channel?: string,
  assembly_id?: number,
  daypart?: number,
  start_date?: string,
  end_date?: string,
) => {
  dataLayer.push({
    channel,
    assembly_id,
    daypart,
    start_date,
    end_date,
    location_id,
    location_group_id,
    event: location_id
      ? GTMEventNames.LOCATION_SCHEDULE_ASSEMBLY
      : GTMEventNames.LOCATION_GROUP_SCHEDULE_ASSEMBLY,
  });
};

export const assemblyEditFromInfoEvent = (assembly_id: number) => {
  dataLayer.push({
    event: GTMEventNames.ASSEMBLY_EDIT_FROMINFO,
    assembly_id,
  });
};

export const deleteAssemblyEvent = (
  assembly_id?: number,
  daypart?: string,
  location_group_id?: number,
  channel?: string,
  location_id?: string,
) => {
  dataLayer.push({
    event: location_id
      ? GTMEventNames.LOCATION_DELETE_ASSEMBLY
      : GTMEventNames.LOCATION_GROUP_DELETE_ASSEMBLY,
    assembly_id,
    daypart,
    location_group_id,
    channel,
    ...(location_id && { location_id }),
  });
};

export const assemblySavedDraftEvent = (assembly_id: number) => {
  dataLayer.push({
    event: GTMEventNames.ASSEMBLY_SAVED_DRAFT,
    assembly_id,
  });
};

export const locationGroupCreateEvent = (
  location_group_id?: string,
  location_group_name?: string,
  layout_type?: string,
  screen_configuration?: ScreenSize[],
) => {
  dataLayer.push({
    event: GTMEventNames.LOCATION_GROUP_CREATED,
    location_group_id,
    location_group_name,
    layout_type,
    screen_configuration,
  });
};

export const locationGroupEditTimingsEvent = (
  location_group_id?: number,
  dayparts?: IDaypart[],
  channel?: string,
) => {
  dataLayer.push({
    event: GTMEventNames.LOCATION_GROUP_EDIT_TIMINGS,
    location_group_id,
    dayparts,
    channel,
  });
};
export const locationEditTimingsEvent = (
  location_id?: number,
  daypart?: IDaypart[],
  channel?: string,
) => {
  dataLayer.push({
    event: GTMEventNames.LOCATION_EDIT_TIMINGS,
    location_id,
    daypart,
    channel,
  });
};

export const assemblyAddComponentEvent = (
  assembly_id: string,
  component_id: number,
) => {
  dataLayer.push({
    event: GTMEventNames.ASSEMBLY_ADD_COMPONENT,
    assembly_id,
    component_id,
  });
};

export const duplicateAssemblyCreationEvent = (
  old_assembly_id: number,
  new_assembly_id: number,
  assembly_name: string,
  layout_type: string,
  layout_name: string,
) => {
  dataLayer.push({
    event: GTMEventNames.ASSEMBLY_DUPLICATED,
    assembly_id_old: old_assembly_id,
    assembly_id_new: new_assembly_id,
    new_assembly_name: assembly_name,
    layout_type,
    layout_name,
  });
};
export const assignLocationTagToLocationEvent = (
  location_id: string,
  location_tag_id: number,
) => {
  dataLayer.push({
    event: GTMEventNames.OO_ASSIGN_TAG_TO_LOCATION,
    location_id,
    location_tag_id,
  });
};
export const locationGroupAssignLocationEvent = (
  location_group_id?: number[],
  channel?: string,
) => {
  dataLayer.push({
    event: GTMEventNames.LOCATION_GROUP_ASSIGN_LOCATION,
    location_group_id,
    channel,
  });
};

export const componentDuplicatedFromCard = (
  component_id_new: number,
  component_id_old: number,
  component_type: string,
  new_component_name: string,
  layout_type: string,
) => {
  dataLayer.push({
    event: GTMEventNames.COMPONENT_DUPLICATED_FROMCARD,
    component_id_new,
    component_id_old,
    component_type,
    new_component_name,
    layout_type,
  });
};

// OO GTM Tags
export const ooOverrideEvent = (
  assembly_id: number | undefined,
  component_id_old: number | undefined,
  component_id_new: number | undefined,
  start_date: string | null,
  location_id: string,
  daypart: DayPartEnum,
  end_date?: string | null,
  channel?: string | null,
) => {
  dataLayer.push({
    event: GTMEventNames.OO_OVERRIDE,
    assembly_id,
    component_id_old,
    component_id_new,
    start_date,
    location_id,
    daypart,
    end_date,
    channel,
  });
};

export const OOLocationTagRemovedLocationEvent = (
  location_id?: string,
  location_tag_id?: number,
) => {
  dataLayer.push({
    event: GTMEventNames.OO_LOCATION_TAG_REMOVED_LOCATION,
    location_id,
    location_tag_id,
  });
};

export const ooCreateAndAssignNewLocationTag = (
  location_id: string,
  location_tag_description: string,
  location_tag_id?: number,
) => {
  dataLayer.push({
    event: GTMEventNames.OO_NEW_LOCATION_TAG_CREATED,
    location_id,
    location_tag_id,
    location_tag_description,
  });
};

export const OOLocationEditTimingsEvent = (
  location_id: string,
  daypart: IDaypart,
  day_of_week: string,
  channel: string,
) => {
  dataLayer.push({
    event: GTMEventNames.OO_LOCATION_EDIT_TIMINGS,
    location_id,
    daypart,
    day_of_week,
    channel,
  });
};

export const ooOverrideDeleteEvent = (
  assembly_id: number | undefined,
  daypart: DayPartEnum,
  location_id?: string,
  channel?: string | null,
) => {
  dataLayer.push({
    event: GTMEventNames.OO_OVERRIDE_DELETE,
    assembly_id,
    daypart,
    location_id,
    channel,
  });
};

export const scheduleComponentDeleteEvent = (
  schedule_override_id: number | undefined,
) => {
  dataLayer.push({
    event: GTMEventNames.SCHEDULE_COMPONENT_DELETE,
    schedule_override_id,
  });
};
